/**
 * @typedef {{
 *     formId: string;
 *     currentStep: number;
 *     addableTabsData: Record<string, boolean[]>;
 *     expires: number;
 *     data: {};
 * }} PersistedForm
 */
/**
 * Return all data from localStorage
 * @returns {PersistedForm[]}
 */
function getAllPersistedForms() {
	const persistedDataRaw = localStorage.getItem('contract-forms');
	return JSON.parse(persistedDataRaw) ?? [];
}

/**
 *
 * @param {string} formId
 * @returns {{form: PersistedForm, index: number}|false}
 */
function getPersistedForm(formId) {
	const persistedForms = getAllPersistedForms();
	const persistedFormIndex = persistedForms.findIndex((formEntry) => formEntry.formId === formId);
	if (persistedFormIndex < 0) return false;
	const persistedForm = persistedForms[persistedFormIndex];

	// Check expiry date
	const now = new Date();
	const expires = new Date(persistedForm.expires);
	if (now.getTime() > expires.getTime()) {
		removePersistedForm(persistedForm.formId);
		return false;
	}

	return {
		form: persistedForm, index: persistedFormIndex,
	};
}

/**
 * Persist form in local storage
 * @param {string} formId
 * @param {number} currentStep
 * @param {object} data
 */
function persistForm(formId, currentStep, data) {
	/**
	 * @type {HTMLFormElement} formElement
	 */
	const formElement = document.getElementById(formId);
	const persistedForms = getAllPersistedForms();
	const daysToExpire = 2;
	const expires = new Date().setDate(new Date().getDate() + daysToExpire);

	/**
	 * Persist addable tabs, by setting an array with boolean values
	 *
	 * `true` if visible/`false` if disabled
	 *
	 * @example
	 * const exampleAddableTabsData = {
	 *     'unique-addable-tab-id': [true, true, false, false],
	 * };
	 */
	const addableTabsData = {};
	const addableTabsContainerElements = formElement.querySelectorAll('.addable-tabs[data-unique-id]');
	if (addableTabsContainerElements) {
		addableTabsContainerElements.forEach((addableTabsContainerElement) => {
			const uniqueId = addableTabsContainerElement.dataset.uniqueId;
			const navButtons = addableTabsContainerElement.querySelectorAll('.addable-nav-tabs button[role="tab"]');
			addableTabsData[uniqueId] = [...navButtons].map((navButton) => !navButton.classList.contains('disabled'));
		})
	}

	/**
	 * @type {PersistedForm}
	 */
	const newForm = { formId, currentStep, addableTabsData, expires, data };

	// Check if form is already persisted
	const alreadyPersistedForm = getPersistedForm(formId);
	if (alreadyPersistedForm) {
		persistedForms[alreadyPersistedForm.index] = newForm;
	} else {
		persistedForms.push(newForm);
	}

	localStorage.setItem('contract-forms', JSON.stringify(persistedForms));
}

function removePersistedForm(formId) {
	const persistedForms = getAllPersistedForms();

	if (persistedForms.length) {
		const filteredPersistedForms = persistedForms.filter((formEntry) => formEntry.formId !== formId);
		localStorage.setItem('contract-forms', JSON.stringify(filteredPersistedForms));
		return true;
	}

	return false;
}

function recoverPersistedForm(form, store) {
	if (!form) return;
	const formId = form.id;
	const persistedForm = getPersistedForm(formId);

	const { data = {}, currentStep = '0', addableTabsData = {} } = persistedForm?.form || {};

	const dataWithQueryParams = updateDataWithQueryParams(form, data);

	if (dataWithQueryParams) {
		// Handle addable tabs
		for (const [uniqueId, tabsState] of Object.entries(addableTabsData)) {
			const addableTabsContainerElement = form.querySelector(`.addable-tabs[data-unique-id="${uniqueId}"]`);
			if (addableTabsContainerElement) {
				const navButtons = addableTabsContainerElement.querySelectorAll('.addable-nav-tabs button[role="tab"]');
				navButtons.forEach((navButton, index) => {
					navButton.classList.toggle('disabled', !tabsState[index])
				});
				updateAddableNavTabsDisability(navButtons);
			}
		}

		fillFormWithData(form, dataWithQueryParams);
	}

	if (currentStep) {
		const parsedStep = parseInt(currentStep);
		if (!isNaN(parsedStep)) {
			store.goToTab(parsedStep);
		}
	}
}

function updateDataWithQueryParams(formElement, data) {
	const newData = { ...data };
	const queryParamsInputsNames = [...formElement.querySelectorAll('.input--query-param-value')].map((input) => input?.name ?? '');
	[...new Set(queryParamsInputsNames)].forEach(function (name) {
		const queryParamValue = getQueryParam(name);
		const persistedValue = newData?.[name] ?? '';
		// we are prioritizing persisted value over the one from query params
		if (!persistedValue && queryParamValue) {
			newData[name] = queryParamValue;
		}
	});

	return newData;
}

function fillFormWithData(formElement, data = {}) {
	if (!formElement || !data) {
		console.error('no form element or data', { formElement, data });
		return;
	}

	for (const [key, val] of Object.entries(data)) {
		const inputs = [...formElement.querySelectorAll(`[name="${key}"]`)];
		let input = null;
		if (inputs.length === 1) {
			input = inputs[0];
		} else if (inputs.length > 1) {
			input = inputs.find((inputElement) => inputElement.value === val)
		}

		if (input) {
			if (['checkbox', 'radio'].includes(input?.type)) {
				input.checked = input.value === val;
				input.dispatchEvent(new Event("change"));
			} else {
				if (input.value !== val) {
					input.value = val;

					// root
					const inputName = input.name; // name = 'kod-pocztowy'
					const autocompleteRootType = input.dataset?.autocompleteRoot ?? ''; // post_code

					if (autocompleteRootType) {
						const CACHE_KEY = `autocomplete-groups`;
						/**
						 * @type {AutocompleteGroup} group
						 */
						const groups = cache().getItem(CACHE_KEY);
						const group = groups?.[inputName];
						if (group) {
							group.updateQueryObject({ [autocompleteRootType]: val.trim() })
							group.updateFieldsDisability();
						}
					} else if (input.classList.contains('input--autocompletefield')) {
						const { autocompleteType = '', autocompleteGroupRoot = '' } = input.dataset;
						const CACHE_KEY = `autocomplete-groups`;
						/**
						 * @type {AutocompleteGroup} group
						 */
						const groups = cache().getItem(CACHE_KEY);
						const group = groups?.[autocompleteGroupRoot];
						if (group && autocompleteType && autocompleteGroupRoot) {
							group.updateQueryObject({ [autocompleteType]: val.trim() });
							group.updateFieldsDisability();
						}
					} else {
						input.dispatchEvent(new Event("input"));
					}
				}
			}
		}
	}
}
